/**
 * LOGIN PAGE (SWA VERSION)
 * =======================
 * This login page uses Azure Static Web Apps built-in authentication.
 * No MSAL - authentication is handled by SWA infrastructure.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthContext';
import { useTranslation } from '@/hooks/useTranslation.js';
import LanguageSwitch from '@/components/Layout/LanguageSwitch';
import { getAppBranding, isAiPmBrandingHost } from '@/utils/appBranding';

const Login: React.FC = () => {
  const { login, isAuthenticated, isLoading, authError } = useAuth();
  const [localLoading, setLocalLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const branding = getAppBranding();
  const showAiPmBranding = isAiPmBrandingHost();
  const accessItems = showAiPmBranding
    ? [
        'Company knowledge chat',
        'Read-only PMapp answers',
        'SharePoint file routing',
        'Admin AI controls',
      ]
    : [
        'Client Management',
        'Project Tracking',
        'Invoice Management',
        'Team Collaboration',
      ];

  console.log('🔍 Login component state:', {
    from: (location.state as any)?.from?.pathname || "/",
    locationState: location.state,
    currentPath: location.pathname,
    isAuthenticated,
    isLoading
  });

  // After auth completes, navigate away from /login
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const from = (location.state as any)?.from?.pathname || "/";
      console.log('✅ Login: User authenticated, navigating to:', from);
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, location.state, navigate]);

  const handleLogin = async () => {
    console.log('🔑 Login button clicked');
    setLocalLoading(true);

    // Get the target URL for post-login redirect
    const from = (location.state as any)?.from?.pathname || "/";

    // Call SWA login - this will redirect to Azure AD
    login(from);

    // Note: login() redirects away, so we won't reach here
    // But keep setLocalLoading for UI consistency
  };

  const showLoading = isLoading || localLoading;
  const error = authError;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--color-background)',
      fontFamily: 'var(--font-family)'
    }}>
      {/* Language Switch */}
      <LanguageSwitch />

      <div style={{
        backgroundColor: 'var(--color-surface)',
        padding: '48px',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-md)',
        width: '400px',
        textAlign: 'center'
      }}>
        {/* Logo/Title */}
        <div style={{
          marginBottom: '32px'
        }}>
          {showAiPmBranding ? (
            <img
              src={branding.appleTouchIconHref}
              alt={`${branding.productName} logo`}
              style={{
                width: '72px',
                height: '72px',
                marginBottom: '16px',
                borderRadius: '18px',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.10)'
              }}
            />
          ) : (
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>
              📋
            </div>
          )}
          <h1 style={{
            margin: 0,
            fontSize: '28px',
            fontWeight: '600',
            color: 'var(--color-text)',
            marginBottom: '8px'
          }}>
            {branding.loginTitle}
          </h1>
          <p style={{
            margin: 0,
            fontSize: '16px',
            color: 'var(--color-text-muted)'
          }}>
            {branding.loginSubtitle}
          </p>
        </div>

        {/* Login Form */}
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: 'var(--color-text)',
            marginBottom: '24px'
          }}>
            Sign in to continue
          </h2>

          {error && (
            <div style={{
              backgroundColor: 'var(--color-danger-soft)',
              border: '1px solid rgba(201, 53, 53, 0.24)',
              color: 'var(--color-danger)',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <strong>Login Error:</strong> {error}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={showLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '16px 24px',
              backgroundColor: showLoading ? '#94A3B8' : '#2251B4',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: showLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              ...(showLoading ? {} : {
                ':hover': {
                  backgroundColor: '#173B84'
                }
              })
            }}
            onMouseEnter={(e) => {
              if (!showLoading) {
                e.currentTarget.style.backgroundColor = '#173B84';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showLoading) {
                e.currentTarget.style.backgroundColor = '#2251B4';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 11V17C20 18.1046 19.1046 19 18 19H2C0.89543 19 0 18.1046 0 17V11" fill="#2251B4"/>
              <path d="M20 4V10C20 8.89543 19.1046 8 18 8H2C0.89543 8 0 8.89543 0 10V4C0 2.89543 0.89543 2 2 2H18C19.1046 2 20 2.89543 20 4Z" fill="#F5F8FC"/>
              <path d="M7.6 15.5H12.4C12.7314 15.5 13 15.2314 13 14.9V11.1C13 10.7686 12.7314 10.5 12.4 10.5H7.6C7.26863 10.5 7 10.7686 7 11.1V14.9C7 15.2314 7.26863 15.5 7.6 15.5Z" fill="white"/>
            </svg>

            {showLoading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Signing in...
              </>
            ) : (
              'Sign in with Microsoft'
            )}
          </button>
        </div>

        {/* Info */}
        <div style={{
          fontSize: '14px',
          color: 'var(--color-text-muted)',
          lineHeight: '1.5'
        }}>
          <p style={{ margin: '0 0 8px 0' }}>
            Sign in with your Microsoft account to access:
          </p>
          <div style={{
            textAlign: 'left',
            display: 'inline-block'
          }}>
            {accessItems.map((item) => (
              <div key={item}>• {item}</div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Login;
